import {
  CheckInput,
  DateInput,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import SectionTitle from '../../../../../../components/text/SectionTitle';
import { useValidations } from '../../../../../../hooks/useValidations';
import { BenefitTypeOption } from '../../../api/discount';

const prefix = 'tariffAndTuition.discounts';

// Agrupación de tipos de aplicación por el campo de valor que usan (HU 16116 v2).
const PERCENTAGE_TYPES = ['porcentaje_fijo', 'porcentaje_maximo'];
const AMOUNT_TYPES = ['monto_fijo', 'monto_maximo'];
// Tipo de aplicación al que se fija el beneficio "Descuento por pronto pago".
const PRONTO_PAGO_TYPE = 'porcentaje_fijo';
const PRONTO_PAGO_CODE = 'DESCUENTO_PRONTO_PAGO';
const DESCRIPTION_MAX_LENGTH = 250;

interface FormValues {
  code: string;
  name: string;
  description: string;
  benefitType: SelectOptionType | null;
  applyTo: SelectOptionType | null;
  applicationType: SelectOptionType | null;
  applicationValue: string;
  canCombineWithOtherBenefits: boolean;
  hasAutomaticAssignment: boolean;
  minimumGradeAverage: string;
  earlyPaymentScope: SelectOptionType | null;
  earlyPaymentCashFrom: string;
  earlyPaymentCashTo: string;
  earlyPaymentInstalmentsFrom: string;
  earlyPaymentInstalmentsTo: string;
}

interface DiscountDataSectionProps {
  editing: boolean;
  benefitTypes: BenefitTypeOption[];
}

const DiscountDataSection = ({
  editing,
  benefitTypes,
}: DiscountDataSectionProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<FormValues>();
  const { msgValidations, validateTextNotEmpty } = useValidations();

  const benefitTypeOptions: SelectOptionType[] = benefitTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  // Opciones de enums fijos (etiquetadas por i18n).
  const applyToOptions: SelectOptionType[] = useMemo(
    () => [
      { value: 'arancel', label: t(`${prefix}.benefit.applyToArancel`) },
      { value: 'matricula', label: t(`${prefix}.benefit.applyToMatricula`) },
      { value: 'ambos', label: t(`${prefix}.benefit.applyToBoth`) },
    ],
    [t],
  );
  const applicationTypeOptions: SelectOptionType[] = useMemo(
    () => [
      {
        value: 'porcentaje_fijo',
        label: t(`${prefix}.benefit.percentageFixed`),
      },
      {
        value: 'porcentaje_maximo',
        label: t(`${prefix}.benefit.percentageMax`),
      },
      { value: 'monto_fijo', label: t(`${prefix}.benefit.amountFixed`) },
      { value: 'monto_variable', label: t(`${prefix}.benefit.amountVariable`) },
      { value: 'monto_maximo', label: t(`${prefix}.benefit.amountMax`) },
    ],
    [t],
  );

  // El tipo de beneficio "Descuento por pronto pago" fuerza el tipo de aplicación
  // a "porcentaje fijo" y bloquea el select (el valor es un porcentaje fijo).
  const benefitType = useWatch({ control, name: 'benefitType' });
  const selectedBenefitCode = benefitTypes.find(
    (type) => type.id === benefitType?.value,
  )?.code;
  const isProntoPago = selectedBenefitCode === PRONTO_PAGO_CODE;

  // Opciones y condiciones del bloque de pronto pago (HU 16376).
  const earlyPaymentScopeOptions: SelectOptionType[] = useMemo(
    () => [
      { value: 'cash', label: t(`${prefix}.benefit.earlyPaymentScopeCash`) },
      {
        value: 'instalments',
        label: t(`${prefix}.benefit.earlyPaymentScopeInstalments`),
      },
      { value: 'both', label: t(`${prefix}.benefit.earlyPaymentScopeBoth`) },
    ],
    [t],
  );

  const hasAutomaticAssignment = useWatch({
    control,
    name: 'hasAutomaticAssignment',
  });
  const earlyPaymentScope = useWatch({ control, name: 'earlyPaymentScope' });
  const scopeValue = earlyPaymentScope?.value as string | undefined;
  const showEarlyPayment = isProntoPago && hasAutomaticAssignment;
  const showCashRange = scopeValue === 'cash' || scopeValue === 'both';
  const showInstalmentsRange =
    scopeValue === 'instalments' || scopeValue === 'both';

  // El campo de valor cambia de label/visibilidad según el tipo de aplicación.
  const applicationType = useWatch({ control, name: 'applicationType' });
  const selectedType = applicationType?.value as string | undefined;
  const isPercentageType = PERCENTAGE_TYPES.includes(selectedType ?? '');
  const isAmountType = AMOUNT_TYPES.includes(selectedType ?? '');
  const showValue = isPercentageType || isAmountType;
  const valueLabel = isAmountType
    ? t(`${prefix}.benefit.amount`)
    : t(`${prefix}.benefit.percentageValue`);

  // Al elegir "Pronto pago" se fija el tipo de aplicación a porcentaje fijo
  // (el select queda bloqueado). Al salir se re-habilita conservando el valor.
  useEffect(() => {
    if (isProntoPago) {
      setValue('applicationType', {
        value: PRONTO_PAGO_TYPE,
        label: t(`${prefix}.benefit.percentageFixed`),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProntoPago]);

  // Al ocultarse el bloque de pronto pago (cambio de tipo de beneficio o al
  // desmarcar la asignación automática) se limpian sus campos: react-hook-form
  // conserva por defecto el valor de los campos desmontados, así que sin este
  // reset un submit posterior enviaría condiciones de pronto pago obsoletas
  // para un descuento que ya no es de ese tipo.
  useEffect(() => {
    if (!showEarlyPayment) {
      setValue('earlyPaymentScope', null);
      setValue('earlyPaymentCashFrom', '');
      setValue('earlyPaymentCashTo', '');
      setValue('earlyPaymentInstalmentsFrom', '');
      setValue('earlyPaymentInstalmentsTo', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEarlyPayment]);

  return (
    <>
      <Row className="mb-3">
        <Col xs="12">
          <SectionTitle text={t(`${prefix}.benefitData`)} />
        </Col>
      </Row>

      {/* Fila 1: Tipo de beneficio | Nombre | Código */}
      <Row className="mb-3">
        <Col xs="12" md="4">
          <Select
            name="benefitType"
            label={t(`${prefix}.benefit.type`)}
            options={benefitTypeOptions}
            isClearable={false}
            control={control}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs="12" md="4">
          <TextInput
            name="name"
            disabled={editing}
            label={t(`${prefix}.benefit.name`)}
            control={control}
            rules={{
              required: msgValidations.required,
              maxLength: {
                value: 50,
                message: t('common.validations.maxLength', { length: 50 }),
              },
              minLength: {
                value: 3,
                message: t('common.validations.minLength', { length: 3 }),
              },
              validate: validateTextNotEmpty,
            }}
          />
        </Col>
        <Col xs="12" md="4">
          <TextInput
            name="code"
            control={control}
            disabled={editing}
            label={t('common.forms.code')}
            rules={{
              required: msgValidations.required,
              maxLength: {
                value: 15,
                message: t('common.validations.maxLength', { length: 15 }),
              },
              minLength: {
                value: 3,
                message: t('common.validations.minLength', { length: 3 }),
              },
              validate: validateTextNotEmpty,
            }}
          />
        </Col>
      </Row>

      {/* Fila 2: Aplicar a | Tipo de aplicación | Monto/Porcentaje */}
      <Row className="mb-3">
        <Col xs="12" md="4">
          <Select
            name="applyTo"
            label={t(`${prefix}.benefit.applyTo`)}
            options={applyToOptions}
            isClearable={false}
            control={control}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs="12" md="4">
          <Select
            name="applicationType"
            label={t(`${prefix}.benefit.applicationType`)}
            options={applicationTypeOptions}
            isClearable={false}
            disabled={isProntoPago}
            control={control}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        {showValue && (
          <Col xs="12" md="4">
            <TextInput
              name="applicationValue"
              control={control}
              label={valueLabel}
              formatter={(value: string) => {
                const digits = Number(value.replace(/\D/g, ''));
                if (!digits) {
                  return '';
                }
                // Porcentaje acotado a 1-100; monto sin tope.
                if (isPercentageType && (digits < 1 || digits > 100)) {
                  return '';
                }
                return String(digits);
              }}
              rules={{
                required: msgValidations.required,
                validate: validateTextNotEmpty,
              }}
            />
          </Col>
        )}
      </Row>

      <Row className="mb-4">
        <Col xs="12">
          <TextAreaInput
            name="description"
            control={control}
            label={`${t('common.forms.description')}`}
            height={120}
            placeholder={t(`common.forms.descriptionPlaceholder`)}
            showCharCounter
            maxLength={DESCRIPTION_MAX_LENGTH}
            rules={{
              maxLength: {
                value: DESCRIPTION_MAX_LENGTH,
                message: t('common.validations.maxLength', {
                  length: DESCRIPTION_MAX_LENGTH,
                }),
              },
            }}
          />
        </Col>
      </Row>

      <hr className="my-4" />

      <Row className="mb-2">
        <Col xs="12">
          <SectionTitle text={t(`${prefix}.automaticAssignmentTitle`)} />
        </Col>
      </Row>
      <Row>
        <Col xs="12">
          <CheckInput
            name="hasAutomaticAssignment"
            label={t(`${prefix}.hasAutomaticAssignment`)}
            control={control}
          />
        </Col>
      </Row>

      {showEarlyPayment && (
        <>
          <Row className="mb-3 mt-4">
            <Col xs="12">
              <SectionTitle text={t(`${prefix}.benefit.earlyPaymentTitle`)} />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs="12" md="4">
              <Select
                name="earlyPaymentScope"
                label={t(`${prefix}.benefit.earlyPaymentScope`)}
                options={earlyPaymentScopeOptions}
                isClearable={false}
                control={control}
                rules={{ required: msgValidations.required }}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col xs="12">
              <p className="text-muted mb-0">
                {t(`${prefix}.benefit.earlyPaymentDatesHelp`)}
              </p>
            </Col>
          </Row>
          {showCashRange && (
            <Row className="mb-3">
              <Col xs="12" md="4">
                <DateInput
                  name="earlyPaymentCashFrom"
                  control={control}
                  label={`${t(`${prefix}.benefit.earlyPaymentCashRange`)} — ${t(
                    `${prefix}.benefit.earlyPaymentDateFrom`,
                  )}`}
                />
              </Col>
              <Col xs="12" md="4">
                <DateInput
                  name="earlyPaymentCashTo"
                  control={control}
                  label={`${t(`${prefix}.benefit.earlyPaymentCashRange`)} — ${t(
                    `${prefix}.benefit.earlyPaymentDateTo`,
                  )}`}
                />
              </Col>
            </Row>
          )}
          {showInstalmentsRange && (
            <Row className="mb-3">
              <Col xs="12" md="4">
                <DateInput
                  name="earlyPaymentInstalmentsFrom"
                  control={control}
                  label={`${t(
                    `${prefix}.benefit.earlyPaymentInstalmentsRange`,
                  )} — ${t(`${prefix}.benefit.earlyPaymentDateFrom`)}`}
                />
              </Col>
              <Col xs="12" md="4">
                <DateInput
                  name="earlyPaymentInstalmentsTo"
                  control={control}
                  label={`${t(
                    `${prefix}.benefit.earlyPaymentInstalmentsRange`,
                  )} — ${t(`${prefix}.benefit.earlyPaymentDateTo`)}`}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

export default DiscountDataSection;
